// skills/farming/farmingUI.js
// Farming UI rendering and interaction - Passive plot-based system

import { farmingSkill } from './farming.js';
import { GRASS_PATCH, CROPS, HERBS, TREES, TOOLS, FARM_CONFIG } from './farmingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatNumber, formatTime, formatWithCommas } from '../../utils/format.js';
import { isBloomholdEligible, getBloomholdStats, getBloomholdContents } from '../../systems/bloomhold.js';
import { toastWarning } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getItemIcon, getItemColor, getItemName, getItem } from '../../data/items/index.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentFlatBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';
import { getAtlasPercentBg } from '../../data/atlasManifest.js';

// Current tab state
let currentTab = 'plots';

/**
 * Get sprite HTML for a tree (if sprite exists)
 */
function getTreeSprite(tree, scale = 0.75) {
    if (!tree.sprite) return '🌳';

    const { sheet, x = 0, y = 0, width = 64, height = 64 } = tree.sprite;
    const isLarge = width >= 48 || height >= 48;
    const sizeClass = isLarge ? ' sprite-icon-lg' : '';
    const isSingleImage = x === 0 && y === 0;

    const marginX = Math.round((width * (1 - scale)) / 2);
    const marginY = Math.round((height * (1 - scale)) / 2);
    const scaleStyle = ` transform: scale(${scale}); margin: -${marginY}px -${marginX}px;`;

    if (isSingleImage) {
        const atlas = getAtlasPercentBg(sheet);
        if (atlas) {
            return `<span class="sprite-icon ${sizeClass}" style="background-image: url('${atlas.atlas}'); background-size: ${atlas.bgSize}; background-position: ${atlas.bgPosition}; width: ${width}px; height: ${height}px;${scaleStyle}" data-hd-src="${sheet}"></span>`;
        }
    }
    const bgSize = isSingleImage ? 'background-size: contain;' : '';
    return `<span class="sprite-icon${sizeClass}" style="background-image: url('${sheet}'); background-position: -${x}px -${y}px; width: ${width}px; height: ${height}px; ${bgSize}${scaleStyle}"></span>`;
}

/**
 * Get a clean sprite span (no inline transform) for use in tab icons
 * CSS handles scaling via .tab-crop-icon container
 */
function getCleanSprite(spriteData) {
    if (!spriteData) return '';
    const { sheet, x = 0, y = 0, width = 64, height = 64 } = spriteData;
    const isSingleImage = x === 0 && y === 0;
    if (isSingleImage) {
        const atlas = getAtlasPercentBg(sheet);
        if (atlas) {
            return `<span class="sprite-icon sprite-icon-lg" style="background-image: url('${atlas.atlas}'); background-size: ${atlas.bgSize}; background-position: ${atlas.bgPosition}; width: ${width}px; height: ${height}px;"></span>`;
        }
    }
    const bgSize = isSingleImage ? 'background-size: contain;' : '';
    return `<span class="sprite-icon sprite-icon-lg" style="background-image: url('${sheet}'); background-position: -${x}px -${y}px; width: ${width}px; height: ${height}px; ${bgSize}"></span>`;
}

/**
 * Get plot icon HTML - uses sprite if available, falls back to emoji
 */
function getPlotIcon(cropData, cropType) {
    if (cropData?.sprite) {
        return `<div class="plot-icon has-sprite">${getTreeSprite(cropData, 0.65)}</div>`;
    }
    const emoji = cropType === 'tree' ? '🌳' : cropType === 'herb' ? '🌿' : '🌱';
    return `<div class="plot-icon" style="background-color: ${cropData?.color || '#7cba5f'};">${emoji}</div>`;
}

/**
 * Get plot icon HTML for ready state - uses sprite if available
 */
function getPlotIconReady(cropData, cropType) {
    if (cropData?.sprite) {
        return `<div class="plot-icon has-sprite pulsing">${getTreeSprite(cropData, 0.65)}</div>`;
    }
    const emoji = cropType === 'tree' ? '🌳' : cropType === 'herb' ? '🌿' : '🥬';
    return `<div class="plot-icon pulsing" style="background-color: ${cropData?.color || '#7cba5f'};">${emoji}</div>`;
}

// Track which plot is being planted (for modal)
let selectedPlotIndex = null;

// Track if plant all modal is open
let plantAllModalOpen = false;

// Track which tab is selected in the plant modal
let plantModalTab = 'crops';

// Raking section minimized state (persisted)
const RAKING_MIN_KEY = 'idleRealms_raking_minimized';
let rakingMinimized = localStorage.getItem(RAKING_MIN_KEY) === 'true';

// Toolbar expanded state (persisted, default open)
const TOOLBAR_KEY = 'idleRealms_farming_toolbar';
let toolbarExpanded = localStorage.getItem(TOOLBAR_KEY) !== 'false';

/**
 * Format time remaining in ms to readable string
 */
function formatTimeRemaining(ms) {
    if (ms <= 0) return 'Ready!';
    const seconds = Math.ceil(ms / 1000);
    return formatTime(seconds);
}

/**
 * Format growth time for display
 */
function formatGrowthTime(ms) {
    const minutes = Math.floor(ms / 60000);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainMins = minutes % 60;
    if (remainMins === 0) return `${hours}h`;
    return `${hours}h ${remainMins}m`;
}

/**
 * Render compact header with tool upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.FARMING);
    const xpPerHour = getXPPerHour(SKILLS.FARMING);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.FARMING);

    // Tool info (farming uses loot multiplier)
    const activeTool = farmingSkill.getActiveBuff();
    const tools = Object.values(TOOLS);
    const currentIndex = tools.findIndex(t => t.id === activeTool.id);
    const nextTool = tools[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextTool) {
        const canAfford = playerGold >= nextTool.price;
        if (skill.level < nextTool.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextTool.name} (${nextTool.lootMultiplier}x yield)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextTool.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.farmingUI.upgradeTool('${nextTool.id}')">
                    → ${nextTool.name} (${nextTool.lootMultiplier}x yield)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.FARMING)}</span>
                    <span class="compact-name">Farming</span>
                    ${renderSkillInfo(SKILLS.FARMING)}
                    ${renderTalentButton(SKILLS.FARMING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Rake:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.lootMultiplier}x harvest yield)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${progressPercent}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render the grass raking card (active action) - gatherer tile style with minimize toggle
 */
function renderRakingCard() {
    const currentAction = gameState.getCurrentAction();
    const isRaking = currentAction?.type === 'farming' && currentAction?.subType === 'raking';
    const progress = isRaking ? (currentAction?.progress || 0) * 100 : 0;

    // Minimized view - compact button
    if (rakingMinimized) {
        return `
            <div class="raking-minimized ${isRaking ? 'active' : ''}" onclick="window.farmingUI.toggleRakingMinimized()">
                <span class="raking-min-icon">🌿</span>
                <span class="raking-min-name">Grass Patch</span>
                ${isRaking ? `<span class="raking-min-status">Raking</span>` : ''}
                <span class="raking-min-expand">▸</span>
            </div>
        `;
    }

    // Calculate total weight for drop chance percentages
    const totalWeight = GRASS_PATCH.drops.reduce((sum, d) => sum + d.weight, 0);

    // Separate crop and herb seeds
    const cropSeeds = GRASS_PATCH.drops.filter(d => !d.itemId.includes('leaf') && !d.itemId.includes('bloom') && !d.itemId.includes('root'));
    const herbSeeds = GRASS_PATCH.drops.filter(d => d.itemId.includes('leaf') || d.itemId.includes('bloom') || d.itemId.includes('root'));

    const effectiveTime = (GRASS_PATCH.baseTime / 1000).toFixed(1);

    // Render seed drops inside card
    const renderSeedDrops = (drops) => drops.map(drop => {
        const icon = getItemIcon(drop.itemId);
        const name = drop.itemId.replace(/_seed$/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        const chance = `${drop.weight}%`;
        return `
            <div class="drop-info seed-drop-item">
                <span class="drop-name">${icon} ${name}</span>
                <span class="drop-chance">${chance}</span>
            </div>
        `;
    }).join('');

    return `
        <div class="raking-grid">
            <div class="raking-tile ${isRaking ? 'active' : ''}"
                 style="--raking-color: ${GRASS_PATCH.color}">
                <div class="raking-tile-header">
                    <span class="raking-tile-level">Lvl ${GRASS_PATCH.level}</span>
                    <button class="raking-minimize-btn" onclick="event.stopPropagation(); window.farmingUI.toggleRakingMinimized()" title="Minimize">◂</button>
                    <span class="raking-tile-icon">🌿</span>
                    <span class="raking-tile-name">${GRASS_PATCH.name}</span>
                </div>

                <div class="raking-tile-info">
                    <div class="raking-tile-stat">
                        <span class="stat-icon farming-icon"></span>
                        <span class="stat-label">XP:</span>
                        <span class="stat-value xp">${GRASS_PATCH.xp}</span>
                    </div>
                    <div class="raking-tile-stat">
                        <span class="stat-icon time-icon"></span>
                        <span class="stat-label">Time:</span>
                        <span class="stat-value">${effectiveTime}s</span>
                    </div>
                </div>

                <div class="raking-tile-drops">
                    <div class="seed-category">
                        <span class="seed-category-label">Crop Seeds (~${totalWeight}%)</span>
                        ${renderSeedDrops(cropSeeds)}
                    </div>
                    <div class="seed-category">
                        <span class="seed-category-label">Herb Seeds</span>
                        ${renderSeedDrops(herbSeeds)}
                    </div>
                </div>

                <div class="raking-tile-action">
                    <button class="action-btn ${isRaking ? 'active' : ''}"
                            onclick="window.farmingUI.toggleRaking()">
                        ${isRaking ? 'Stop Raking' : 'Rake'}
                    </button>
                </div>

                <div class="raking-tile-progress">
                    <div class="raking-tile-progress-fill" style="width: ${progress}%;"></div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Get yield display string for a crop (e.g., "5 Potato" or "10 Ironwood Logs")
 */
function getYieldDisplay(cropData, cropType) {
    if (!cropData) return '';

    // Handle special case of Eldritch tree with random drops
    if (cropData.randomDrops) {
        const logRange = `${cropData.randomDrops.logs.min}-${cropData.randomDrops.logs.max}`;
        return `${logRange} logs`;
    }

    // Normal drops
    if (cropData.drops && cropData.drops.length > 0) {
        const drop = cropData.drops[0];
        const itemName = drop.itemId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        // Shorten common suffixes
        const shortName = itemName.replace(' Logs', '').replace('Grimy ', '');
        return `${drop.quantity} ${shortName}`;
    }
    return '';
}

/**
 * Render a single plot card
 */
function renderPlotCard(plotIndex) {
    const plot = gameState.getFarmPlot(plotIndex);
    const isUnlocked = farmingSkill.isPlotUnlocked(plotIndex);
    const requiredLevel = FARM_CONFIG.UNLOCK_LEVELS[plotIndex];

    if (!isUnlocked) {
        return `
            <div class="plot-card locked">
                <div class="plot-icon">🔒</div>
                <div class="plot-status">Lv ${requiredLevel}</div>
            </div>
        `;
    }

    if (plot.state === 'empty') {
        return `
            <div class="plot-card empty">
                <div class="plot-icon">🌱</div>
                <div class="plot-status">Empty</div>
                <button class="plot-button plant" onclick="window.farmingUI.openPlantModal(${plotIndex})">
                    Plant
                </button>
            </div>
        `;
    }

    if (plot.state === 'growing') {
        const timeRemaining = farmingSkill.getTimeRemaining(plotIndex);
        const progress = farmingSkill.checkPlotProgress(plotIndex) || 0;
        const progressPercent = Math.min(progress * 100, 100);
        const cropData = plot.cropData;
        const yieldDisplay = getYieldDisplay(cropData, plot.cropType);

        return `
            <div class="plot-card growing">
                <button class="plot-remove-btn" onclick="event.stopPropagation(); window.farmingUI.removePlant(${plotIndex})" title="Remove plant (seeds lost)">×</button>
                ${getPlotIcon(cropData, plot.cropType)}
                <div class="plot-name">${cropData?.name || 'Growing'}</div>
                <div class="plot-timer">${formatTimeRemaining(timeRemaining)}</div>
                <div class="plot-progress">
                    <div class="plot-progress-fill" style="width: ${progressPercent}%;"></div>
                </div>
                <div class="plot-yield">→ ${yieldDisplay}</div>
                <div class="plot-xp"><span class="stat-icon farming-icon"></span>+${formatNumber(cropData?.xp || 0)} XP</div>
            </div>
        `;
    }

    if (plot.state === 'ready') {
        const cropData = plot.cropData;
        const yieldDisplay = getYieldDisplay(cropData, plot.cropType);

        return `
            <div class="plot-card ready">
                ${getPlotIconReady(cropData, plot.cropType)}
                <div class="plot-name">${cropData?.name || 'Ready'}</div>
                <div class="plot-yield ready">→ ${yieldDisplay}</div>
                <div class="plot-xp"><span class="stat-icon farming-icon"></span>+${formatNumber(cropData?.xp || 0)} XP</div>
                <button class="plot-button harvest" onclick="window.farmingUI.harvestPlot(${plotIndex})">
                    Harvest
                </button>
            </div>
        `;
    }

    return '';
}

/**
 * Render auto-harvest settings (level 50+) and auto-plant toggle (Eternal Garden talent)
 */
function renderAutoHarvestSettings() {
    const farmSettings = gameState.getFarmSettings();
    const bloomholdUnlocked = gameState.isBloomholdUnlocked();

    // Check if player has Eternal Garden talent (autoReplant)
    const hasAutoReplant = getTalentFlatBonus(SKILLS.FARMING, 'autoReplant') > 0;

    // Auto-plant toggle (only show if they have the talent)
    const autoPlantToggle = hasAutoReplant ? `
        <div class="setting-inline auto-plant-toggle">
            <label class="toggle-label">
                <input type="checkbox"
                       ${farmSettings.autoPlantEnabled ? 'checked' : ''}
                       onchange="window.farmingUI.toggleAutoPlant(this.checked)">
                <span class="toggle-switch"></span>
                Auto-Plant
            </label>
            <span class="setting-hint">(Eternal Garden)</span>
        </div>
    ` : '';

    // If auto-harvest not unlocked, show locked message but still show auto-plant if they have talent
    if (!farmingSkill.isAutoHarvestUnlocked()) {
        if (!hasAutoReplant) {
            return `
                <div class="farm-settings locked">
                    <span class="setting-inline">🔒 Auto-Harvest unlocks at Level ${FARM_CONFIG.AUTO_HARVEST_UNLOCK_LEVEL}</span>
                </div>
            `;
        }
        // Has talent but no auto-harvest yet - show both
        return `
            <div class="farm-settings compact">
                <div class="setting-inline locked-hint">
                    🔒 Auto-Harvest unlocks at Level ${FARM_CONFIG.AUTO_HARVEST_UNLOCK_LEVEL}
                </div>
                ${autoPlantToggle}
            </div>
        `;
    }

    // Compact inline version when auto-harvest is unlocked
    return `
        <div class="farm-settings compact">
            <div class="setting-inline">
                <label class="toggle-label">
                    <input type="checkbox"
                           ${farmSettings.autoHarvestEnabled ? 'checked' : ''}
                           onchange="window.farmingUI.toggleAutoHarvest(this.checked)">
                    <span class="toggle-switch"></span>
                    Auto-Harvest
                </label>
                <span class="setting-separator">→</span>
                <select onchange="window.farmingUI.setAutoHarvestDestination(this.value)"
                        ${!farmSettings.autoHarvestEnabled ? 'disabled' : ''}>
                    <option value="inventory" ${farmSettings.autoHarvestDestination === 'inventory' ? 'selected' : ''}>
                        Inventory
                    </option>
                    <option value="bank" ${farmSettings.autoHarvestDestination === 'bank' ? 'selected' : ''}>
                        Bank
                    </option>
                    ${bloomholdUnlocked ? `
                    <option value="bloomhold" ${farmSettings.autoHarvestDestination === 'bloomhold' ? 'selected' : ''}>
                        🌱 BloomHold
                    </option>
                    ` : ''}
                </select>
            </div>
            ${autoPlantToggle}
        </div>
    `;
}

/**
 * Render BloomHold info panel for Farming
 */
function renderBloomholdInfo() {
    const bloomholdUnlocked = gameState.isBloomholdUnlocked();
    const farmingLevel = gameState.getSkill(SKILLS.FARMING).level;
    const hasGreenhouse = gameState.hasMilestone && gameState.hasMilestone('greenhouse');

    if (bloomholdUnlocked) {
        // Compact display when unlocked - just show slots count inline
        const slots = gameState.getBloomholdSlots();
        const usedSlots = slots.length;
        const maxSlots = gameState.getBloomholdMaxSlots ? gameState.getBloomholdMaxSlots() : 100;

        return `
            <div class="bloomhold-info unlocked compact">
                <span class="bloomhold-icon">🌱</span>
                <span class="bloomhold-label">BloomHold:</span>
                <span class="bloomhold-count">${usedSlots}/${maxSlots} slots</span>
                <span class="bloomhold-hint">• Plant directly from BloomHold • Use "Plant All" to fill plots</span>
            </div>
        `;
    }

    // Not unlocked - show how to unlock (compact)
    const meetsFarmLevel = farmingLevel >= 45;
    const goldCount = gameState.getGold();
    const canAfford = goldCount >= 25000;

    return `
        <div class="bloomhold-info locked">
            <div class="bloomhold-header">
                <span class="bloomhold-icon">🔒</span>
                <span class="bloomhold-title">BloomHold</span>
                <span class="bloomhold-subtitle">Seed & herb vault</span>
            </div>
            <div class="bloomhold-requirements">
                <span class="requirement ${hasGreenhouse ? 'met' : 'unmet'}">${hasGreenhouse ? '✓' : '✗'} Greenhouse</span>
                <span class="requirement ${meetsFarmLevel ? 'met' : 'unmet'}">${meetsFarmLevel ? '✓' : '✗'} Lv45 Farming</span>
                <span class="requirement ${canAfford ? 'met' : 'unmet'}">${canAfford ? '✓' : '✗'} 25k Gold</span>
            </div>
        </div>
    `;
}

/**
 * Format quantity for compact slot display
 */
function formatSlotQty(quantity) {
    if (quantity >= 10000) return formatNumber(quantity);
    return quantity.toLocaleString();
}

/**
 * Render BloomHold item preview as bank-style slot grid
 */
function renderBloomholdPreview() {
    if (!gameState.isBloomholdUnlocked()) return '';

    const contents = getBloomholdContents('quantity');
    const stats = getBloomholdStats();

    if (contents.length === 0) {
        return `
            <div class="bloomhold-preview-grid">
                <div class="bloomhold-preview-empty">No items stored</div>
            </div>
        `;
    }

    // Render all items as bank-style slots
    const slots = contents.map(slot => {
        const icon = getItemIcon(slot.itemId);
        const color = getItemColor(slot.itemId);
        const name = getItemName(slot.itemId);
        const bgStyle = color !== 'transparent' ? `background-color: ${color};` : '';
        const tooltip = `${name}\nQuantity: ${slot.quantity.toLocaleString()}`;
        return `
            <div class="bloomhold-slot" data-tooltip="${tooltip}">
                <div class="bloomhold-slot-content" style="${bgStyle}">
                    <span class="bloomhold-slot-icon">${icon}</span>
                    <span class="bloomhold-slot-qty">${formatSlotQty(slot.quantity)}</span>
                </div>
            </div>
        `;
    }).join('');

    // Always pad to 50 slots (base BloomHold size) for consistent grid
    const targetSlots = 50;
    const emptyCount = Math.max(0, targetSlots - contents.length);
    const emptySlots = Array(emptyCount).fill(
        `<div class="bloomhold-slot empty"><div class="bloomhold-slot-content"></div></div>`
    ).join('');

    return `<div class="bloomhold-preview-grid">${slots}${emptySlots}</div>`;
}

/**
 * Render the farm toolbar widget (auto-harvest, auto-plant, bloomhold preview)
 */
function renderFarmToolbar() {
    const farmSettings = gameState.getFarmSettings();
    const bloomholdUnlocked = gameState.isBloomholdUnlocked();
    const hasAutoHarvest = farmingSkill.isAutoHarvestUnlocked();
    const hasAutoReplant = getTalentFlatBonus(SKILLS.FARMING, 'autoReplant') > 0;

    // Build collapsed chips
    const chips = [];

    // Auto-Harvest chip
    if (hasAutoHarvest) {
        const dest = farmSettings.autoHarvestDestination === 'bloomhold' ? 'BloomHold'
            : farmSettings.autoHarvestDestination === 'bank' ? 'Bank' : 'Inventory';
        const stateLabel = farmSettings.autoHarvestEnabled ? `ON → ${dest}` : 'OFF';
        const stateClass = farmSettings.autoHarvestEnabled ? 'on' : 'off';
        chips.push(`<span class="toolbar-chip harvest ${stateClass}">Auto-Harvest: ${stateLabel}</span>`);
    } else {
        chips.push(`<span class="toolbar-chip harvest locked">Auto-Harvest (Lv ${FARM_CONFIG.AUTO_HARVEST_UNLOCK_LEVEL})</span>`);
    }

    // Auto-Plant chip (only if talent unlocked)
    if (hasAutoReplant) {
        const stateClass = farmSettings.autoPlantEnabled ? 'on' : 'off';
        let plantLabel = farmSettings.autoPlantEnabled ? 'ON' : 'OFF';
        if (farmSettings.autoPlantEnabled && farmSettings.autoPlantMode === 'specific' && farmSettings.autoPlantCropId) {
            const specificData = farmingSkill.getCropData(farmSettings.autoPlantCropType, farmSettings.autoPlantCropId);
            if (specificData) plantLabel = specificData.name;
        }
        chips.push(`<span class="toolbar-chip plant ${stateClass}">Auto-Plant: ${plantLabel}</span>`);
    }


    if (!toolbarExpanded) {
        // Collapsed view
        return `
            <div class="farm-toolbar collapsed" onclick="window.farmingUI.toggleToolbar()">
                <span class="toolbar-toggle">&#9656;</span>
                ${chips.join('<span class="toolbar-chip-sep">|</span>')}
            </div>
        `;
    }

    // Expanded view
    // Auto-Harvest control
    let autoHarvestHtml = '';
    if (hasAutoHarvest) {
        autoHarvestHtml = `
            <div class="toolbar-control">
                <label class="toggle-label">
                    <input type="checkbox"
                           ${farmSettings.autoHarvestEnabled ? 'checked' : ''}
                           onchange="window.farmingUI.toggleAutoHarvest(this.checked)">
                    <span class="toggle-switch"></span>
                    Auto-Harvest
                </label>
                <span class="toolbar-arrow">→</span>
                <select onchange="window.farmingUI.setAutoHarvestDestination(this.value)"
                        ${!farmSettings.autoHarvestEnabled ? 'disabled' : ''}>
                    <option value="inventory" ${farmSettings.autoHarvestDestination === 'inventory' ? 'selected' : ''}>Inventory</option>
                    <option value="bank" ${farmSettings.autoHarvestDestination === 'bank' ? 'selected' : ''}>Bank</option>
                    ${bloomholdUnlocked ? `<option value="bloomhold" ${farmSettings.autoHarvestDestination === 'bloomhold' ? 'selected' : ''}>BloomHold</option>` : ''}
                </select>
            </div>
        `;
    } else {
        autoHarvestHtml = `
            <div class="toolbar-control locked-msg">
                <span>Auto-Harvest unlocks at Level ${FARM_CONFIG.AUTO_HARVEST_UNLOCK_LEVEL}</span>
            </div>
        `;
    }

    // Auto-Plant control
    let autoPlantHtml = '';
    if (hasAutoReplant) {
        const isSpecific = farmSettings.autoPlantMode === 'specific';

        // Build seed picker for 'specific' mode
        let seedPickerHtml = '';
        if (isSpecific && farmSettings.autoPlantEnabled) {
            const skill = gameState.getSkill(SKILLS.FARMING);
            const allCrops = [
                { label: 'Crops', items: Object.entries(CROPS).map(([k, v]) => ({ key: k, data: v, type: 'crop' })) },
                { label: 'Herbs', items: Object.entries(HERBS).map(([k, v]) => ({ key: k, data: v, type: 'herb' })) },
                { label: 'Trees', items: Object.entries(TREES).map(([k, v]) => ({ key: k, data: v, type: 'tree' })) },
            ];

            // Current selection display
            let currentLabel = 'Replant Last';
            let currentIcon = '🌱';
            if (farmSettings.autoPlantCropType && farmSettings.autoPlantCropId) {
                const selData = farmingSkill.getCropData(farmSettings.autoPlantCropType, farmSettings.autoPlantCropId);
                if (selData) {
                    if (skill.level < selData.level) {
                        currentLabel = `${selData.name} (Locked)`;
                        currentIcon = '🔒';
                    } else {
                        currentLabel = selData.name;
                        currentIcon = getItemIcon(selData.seedId);
                    }
                }
            }

            const seedItems = allCrops.map(group => {
                const options = group.items.map(({ key, data, type }) => {
                    const isLocked = skill.level < data.level;
                    const isSelected = !isLocked && farmSettings.autoPlantCropType === type && farmSettings.autoPlantCropId === key;
                    const icon = getItemIcon(data.seedId);
                    if (isLocked) {
                        return `<div class="seed-option locked">🔒 <span>${data.name} (Lv ${data.level})</span></div>`;
                    }
                    return `<div class="seed-option ${isSelected ? 'selected' : ''}" onclick="window.farmingUI.setAutoPlantSeed('${type}:${key}')">${icon} <span>${data.name}</span></div>`;
                }).join('');
                return `<div class="seed-group-label">${group.label}</div>${options}`;
            }).join('');

            seedPickerHtml = `
                <div class="seed-picker">
                    <div class="seed-picker-selected" onclick="window.farmingUI.toggleSeedPicker()">
                        <span class="seed-picker-icon">${currentIcon}</span>
                        <span class="seed-picker-label">${currentLabel}</span>
                        <span class="seed-picker-arrow">&#9662;</span>
                    </div>
                    <div class="seed-picker-dropdown" style="display: none;">
                        <div class="seed-option none" onclick="window.farmingUI.setAutoPlantSeed('')">
                            <span>🌱</span> <span>-- None (use last) --</span>
                        </div>
                        ${seedItems}
                    </div>
                </div>
            `;
        }

        autoPlantHtml = `
            <div class="toolbar-control auto-plant-control">
                <label class="toggle-label">
                    <input type="checkbox"
                           ${farmSettings.autoPlantEnabled ? 'checked' : ''}
                           onchange="window.farmingUI.toggleAutoPlant(this.checked)">
                    <span class="toggle-switch"></span>
                    Auto-Plant
                </label>
                <span class="toolbar-arrow">→</span>
                <select class="auto-plant-mode" onchange="window.farmingUI.setAutoPlantMode(this.value)"
                        ${!farmSettings.autoPlantEnabled ? 'disabled' : ''}>
                    <option value="last" ${!isSpecific ? 'selected' : ''}>Replant Last</option>
                    <option value="specific" ${isSpecific ? 'selected' : ''}>Specific Seed</option>
                </select>
                ${seedPickerHtml}
            </div>
        `;
    }

    // BloomHold section
    let bloomholdHtml = '';
    if (bloomholdUnlocked) {
        const stats = getBloomholdStats();
        bloomholdHtml = `
            <div class="toolbar-bloomhold">
                <div class="toolbar-bloomhold-header">
                    <span class="toolbar-bloomhold-label">BloomHold</span>
                    <span class="toolbar-bloomhold-count"><span class="chip-value">${stats.usedSlots}</span>/${stats.maxSlots} slots</span>
                </div>
                ${renderBloomholdPreview()}
            </div>
        `;
    } else {
        const farmingLevel = gameState.getSkill(SKILLS.FARMING).level;
        const hasGreenhouse = gameState.hasMilestone && gameState.hasMilestone('greenhouse');
        const meetsFarmLevel = farmingLevel >= 45;
        const goldCount = gameState.getGold();
        const canAfford = goldCount >= 25000;

        bloomholdHtml = `
            <div class="toolbar-bloomhold locked">
                <div class="toolbar-bloomhold-header">
                    <span class="toolbar-bloomhold-label">BloomHold</span>
                    <span class="toolbar-bloomhold-subtitle">Seed & herb vault</span>
                </div>
                <div class="toolbar-bloomhold-requirements">
                    <span class="requirement ${hasGreenhouse ? 'met' : 'unmet'}">${hasGreenhouse ? '&#10003;' : '&#10007;'} Greenhouse</span>
                    <span class="requirement ${meetsFarmLevel ? 'met' : 'unmet'}">${meetsFarmLevel ? '&#10003;' : '&#10007;'} Lv45 Farming</span>
                    <span class="requirement ${canAfford ? 'met' : 'unmet'}">${canAfford ? '&#10003;' : '&#10007;'} 25k Gold</span>
                </div>
            </div>
        `;
    }

    return `
        <div class="farm-toolbar expanded">
            <div class="toolbar-header" onclick="window.farmingUI.toggleToolbar()">
                <span class="toolbar-toggle">&#9662;</span>
                <span class="toolbar-title">Farm Controls</span>
            </div>
            <div class="toolbar-body">
                <div class="toolbar-controls">
                    ${autoHarvestHtml}
                    ${autoPlantHtml}
                </div>
                ${bloomholdHtml}
            </div>
        </div>
    `;
}

/**
 * Render the plots tab content
 */
function renderPlotsTab() {
    const plots = [];
    for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
        plots.push(renderPlotCard(i));
    }

    const readyPlots = farmingSkill.getReadyPlots();
    const emptyPlots = farmingSkill.getEmptyPlots();
    const bloomholdUnlocked = gameState.isBloomholdUnlocked();

    const harvestAllButton = readyPlots.length > 1 ? `
        <button class="harvest-all-button" onclick="window.farmingUI.harvestAll()">
            Harvest All (${readyPlots.length})
        </button>
    ` : '';

    // Plant All requires BloomHold unlock
    const plantAllButton = (bloomholdUnlocked && emptyPlots.length > 1) ? `
        <button class="plant-all-button" onclick="window.farmingUI.openPlantAllModal()">
            Plant All (${emptyPlots.length})
        </button>
    ` : '';

    return `
        <div class="farming-section plots-section">
            <div class="plots-header">
                <h3 class="section-title">Farm Plots</h3>
                <div class="plots-header-buttons">
                    ${plantAllButton}
                    ${harvestAllButton}
                </div>
            </div>
            <div class="plots-grid">
                ${plots.join('')}
            </div>
        </div>
    `;
}

/**
 * Render a crop/herb/tree info card for the reference tabs - gatherer tile style
 */
function renderInfoCard(item, type) {
    const skill = gameState.getSkill(SKILLS.FARMING);
    const isLocked = skill.level < item.level;
    const inventoryCount = gameState.getItemCount(item.seedId);
    const bloomholdCount = gameState.isBloomholdUnlocked() ? gameState.getBloomholdItemCount(item.seedId) : 0;
    const totalCount = inventoryCount + bloomholdCount;
    const hasSeeds = totalCount > 0;

    // Level display
    const levelText = isLocked ? `🔒 Lv ${item.level}` : `Lv ${item.level}`;

    // Icon - render at natural 64px like mining's getRockIcon pattern
    let iconHtml;
    if (item.sprite) {
        const spriteHtml = getCleanSprite(item.sprite);
        iconHtml = `<div class="farm-card-icon">${spriteHtml}</div>`;
    } else {
        const icon = type === 'tree' ? '🌳' : type === 'herb' ? '🌿' : '🌱';
        iconHtml = `<div class="farm-card-icon">${icon}</div>`;
    }

    // Show breakdown if BloomHold has seeds
    const seedDisplay = bloomholdCount > 0
        ? `${totalCount} (${inventoryCount}+${bloomholdCount}🌱)`
        : `${totalCount}`;

    // Drops section
    let dropsHtml = '';
    if (item.randomDrops) {
        // Eldritch tree - all log item sprites rendered, CSS cycles through them
        const logsWithSprites = item.randomDrops.logs.pool
            .map(logId => getItem(logId))
            .filter(log => log?.sprite);
        const cyclingSprites = logsWithSprites.map(log =>
            `<span class="eldritch-sprite">${getCleanSprite(log.sprite)}</span>`
        ).join('');
        const logIcon = `<span class="eldritch-cycle-container">${cyclingSprites}</span>`;
        const logRange = `${item.randomDrops.logs.min}-${item.randomDrops.logs.max}`;
        dropsHtml = `
            <div class="drop-info farm-drop">
                <span class="drop-name">${logIcon} Random Logs</span>
                <span class="drop-qty">x${logRange}</span>
            </div>
            <div class="drop-info farm-drop">
                <span class="drop-name">🌱 Random Seeds</span>
                <span class="drop-qty">x${item.randomDrops.seeds.min}-${item.randomDrops.seeds.max}</span>
            </div>
        `;
    } else if (item.drops && item.drops.length > 0) {
        dropsHtml = item.drops.map(drop => {
            const dropIcon = getItemIcon(drop.itemId);
            const dropName = drop.itemId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                .replace(' Logs', '').replace('Grimy ', '');
            return `
                <div class="drop-info farm-drop">
                    <span class="drop-name">${dropIcon} ${dropName}</span>
                    <span class="drop-qty">x${drop.quantity}</span>
                </div>
            `;
        }).join('');
    }

    return `
        <div class="farm-card ${isLocked ? 'locked' : ''} ${!hasSeeds && !isLocked ? 'no-seeds' : ''}"
             style="--farm-color: ${item.color}">
            <div class="farm-card-header">
                <span class="farm-card-level">${levelText}</span>
                ${iconHtml}
                <span class="farm-card-name">${item.name}</span>
            </div>

            <div class="farm-card-info">
                <div class="farm-stat">
                    <span class="stat-icon farming-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp">${formatNumber(item.xp)}</span>
                </div>
                <div class="farm-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value">${formatGrowthTime(item.growthTime)}</span>
                </div>
            </div>

            <div class="farm-drops">
                ${dropsHtml}
            </div>

            <div class="farm-seeds">
                <span class="seed-icon">${getItemIcon(item.seedId)}</span>
                <span class="seed-label">Seeds:</span>
                <span class="seed-count ${hasSeeds ? 'has' : 'none'}">${seedDisplay}</span>
            </div>
        </div>
    `;
}

/**
 * Render the crops tab content
 */
function renderCropsTab() {
    const cropCards = Object.values(CROPS).map(crop => renderInfoCard(crop, 'crop')).join('');

    return `
        <div class="farm-grid">
            ${cropCards}
        </div>
    `;
}

/**
 * Render the herbs tab content
 */
function renderHerbsTab() {
    const herbCards = Object.values(HERBS).map(herb => renderInfoCard(herb, 'herb')).join('');

    return `
        <div class="farm-grid">
            ${herbCards}
        </div>
    `;
}

/**
 * Render the trees tab content
 */
function renderTreesTab() {
    const treeCards = Object.values(TREES).map(tree => renderInfoCard(tree, 'tree')).join('');

    return `
        <div class="farm-grid">
            ${treeCards}
        </div>
    `;
}

/**
 * Render tab navigation
 */
function renderTabs() {
    const cropsTabIcon = `<span class="tab-crop-icon silhouette">${getCleanSprite(CROPS.STRAWBERRY?.sprite)}</span>`;
    const herbsTabIcon = `<span class="tab-crop-icon silhouette">${getCleanSprite(HERBS.GREENLEAF?.sprite)}</span>`;
    const treesTabIcon = `<span class="tab-crop-icon silhouette">${getCleanSprite(TREES.ARCANEWOOD?.sprite)}</span>`;

    return `
        <div class="skill-tabs">
            <button class="skill-tab ${currentTab === 'plots' ? 'active' : ''}"
                    onclick="window.farmingUI.switchTab('plots')">
                🌾 Plots
            </button>
            <button class="skill-tab ${currentTab === 'crops' ? 'active' : ''}"
                    onclick="window.farmingUI.switchTab('crops')">
                ${cropsTabIcon}Crops
            </button>
            <button class="skill-tab ${currentTab === 'herbs' ? 'active' : ''}"
                    onclick="window.farmingUI.switchTab('herbs')">
                ${herbsTabIcon}Herbs
            </button>
            <button class="skill-tab ${currentTab === 'trees' ? 'active' : ''}"
                    onclick="window.farmingUI.switchTab('trees')">
                ${treesTabIcon}Trees
            </button>
        </div>
    `;
}

/**
 * Render plant selection modal
 */
function renderPlantModal() {
    if (selectedPlotIndex === null) return '';

    return `
        <div class="modal-overlay" onclick="window.farmingUI.closePlantModal()">
            <div class="plant-modal" onclick="event.stopPropagation()">
                <div class="modal-header">
                    <h3>Plant in Plot ${selectedPlotIndex + 1}</h3>
                    <button class="modal-close" onclick="window.farmingUI.closePlantModal()">&times;</button>
                </div>
                <div class="plant-modal-tabs">
                    <button class="plant-modal-tab ${plantModalTab === 'crops' ? 'active' : ''}"
                            onclick="window.farmingUI.switchPlantModalTab('crops')">
                        <span class="tab-crop-icon silhouette">${getCleanSprite(CROPS.STRAWBERRY?.sprite)}</span>Crops
                    </button>
                    <button class="plant-modal-tab ${plantModalTab === 'herbs' ? 'active' : ''}"
                            onclick="window.farmingUI.switchPlantModalTab('herbs')">
                        🌿 Herbs
                    </button>
                    <button class="plant-modal-tab ${plantModalTab === 'trees' ? 'active' : ''}"
                            onclick="window.farmingUI.switchPlantModalTab('trees')">
                        <span class="tab-crop-icon silhouette">${getCleanSprite(TREES.ARCANEWOOD?.sprite)}</span>Trees
                    </button>
                </div>
                <div class="plant-options">
                    ${renderPlantOptionsContent()}
                </div>
            </div>
        </div>
    `;
}

/**
 * Render plant all selection modal
 */
function renderPlantAllModal() {
    if (!plantAllModalOpen) return '';

    const emptyCount = farmingSkill.getEmptyPlots().length;

    return `
        <div class="modal-overlay" onclick="window.farmingUI.closePlantAllModal()">
            <div class="plant-modal" onclick="event.stopPropagation()">
                <div class="modal-header">
                    <h3>Plant All (${emptyCount} empty plots)</h3>
                    <button class="modal-close" onclick="window.farmingUI.closePlantAllModal()">&times;</button>
                </div>
                <div class="plant-modal-tabs">
                    <button class="plant-modal-tab ${plantModalTab === 'crops' ? 'active' : ''}"
                            onclick="window.farmingUI.switchPlantModalTab('crops')">
                        <span class="tab-crop-icon silhouette">${getCleanSprite(CROPS.STRAWBERRY?.sprite)}</span>Crops
                    </button>
                    <button class="plant-modal-tab ${plantModalTab === 'herbs' ? 'active' : ''}"
                            onclick="window.farmingUI.switchPlantModalTab('herbs')">
                        🌿 Herbs
                    </button>
                    <button class="plant-modal-tab ${plantModalTab === 'trees' ? 'active' : ''}"
                            onclick="window.farmingUI.switchPlantModalTab('trees')">
                        <span class="tab-crop-icon silhouette">${getCleanSprite(TREES.ARCANEWOOD?.sprite)}</span>Trees
                    </button>
                </div>
                <div class="plant-options">
                    ${renderPlantOptionsContent()}
                </div>
            </div>
        </div>
    `;
}

export function renderFarmingUI(params = {}) {
    // Handle tab from route params
    if (params.tab) {
        currentTab = params.tab;
    }

    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.FARMING);

    let tabContent;
    switch (currentTab) {
        case 'crops':
            tabContent = renderCropsTab();
            break;
        case 'herbs':
            tabContent = renderHerbsTab();
            break;
        case 'trees':
            tabContent = renderTreesTab();
            break;
        default:
            tabContent = renderPlotsTab();
    }

    container.innerHTML = `
    <div class="farming-interface">
      ${renderCompactHeader()}

      <div class="farming-content">
        <div class="farming-top-row${rakingMinimized ? ' raking-is-minimized' : ''}">
            ${renderRakingCard()}
            ${renderFarmToolbar()}
        </div>

        ${renderTabs()}

        <div class="tab-content">
            ${tabContent}
        </div>
      </div>
    </div>
    ${renderPlantModal()}
    ${renderPlantAllModal()}
  `;
}

export function switchTab(tab) {
    currentTab = tab;
    renderFarmingUI();
}

export function toggleRaking() {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.subType === 'raking') {
        farmingSkill.stopRaking();
    } else {
        farmingSkill.startRaking();
    }

    renderFarmingUI();
}

export function openPlantModal(plotIndex) {
    selectedPlotIndex = plotIndex;
    renderFarmingUI();
}

export function closePlantModal() {
    selectedPlotIndex = null;
    plantModalTab = 'crops'; // Reset to default tab
    renderFarmingUI();
}

export function switchPlantModalTab(tab) {
    plantModalTab = tab;
    updatePlantModalContent();
}

/**
 * Update just the plant modal content without full re-render
 */
function updatePlantModalContent() {
    const modal = document.querySelector('.plant-modal');
    if (!modal) return;

    // Update tab button states
    modal.querySelectorAll('.plant-modal-tab').forEach(btn => {
        const tabName = btn.textContent.toLowerCase();
        if (tabName.includes('crops')) {
            btn.classList.toggle('active', plantModalTab === 'crops');
        } else if (tabName.includes('herbs')) {
            btn.classList.toggle('active', plantModalTab === 'herbs');
        } else if (tabName.includes('trees')) {
            btn.classList.toggle('active', plantModalTab === 'trees');
        }
    });

    // Update plant options content
    const optionsContainer = modal.querySelector('.plant-options');
    if (optionsContainer) {
        optionsContainer.innerHTML = renderPlantOptionsContent();
    }
}

/**
 * Render just the plant options (shared by single plot and plant all modals)
 */
function renderPlantOptionsContent() {
    const skill = gameState.getSkill(SKILLS.FARMING);
    const bloomholdUnlocked = gameState.isBloomholdUnlocked();

    let items = [];
    let itemType, icon;

    if (plantModalTab === 'crops') {
        items = Object.entries(CROPS);
        itemType = 'crop';
        icon = '🌱';
    } else if (plantModalTab === 'herbs') {
        items = Object.entries(HERBS);
        itemType = 'herb';
        icon = '🌿';
    } else {
        items = Object.entries(TREES);
        itemType = 'tree';
        icon = '🌳';
    }

    // Determine which onclick to use based on which modal is open
    const isPlantAll = plantAllModalOpen;
    const emptyPlots = isPlantAll ? farmingSkill.getEmptyPlots() : [];
    const emptyCount = emptyPlots.length;

    return items.map(([key, data]) => {
        const canPlant = farmingSkill.canPlant(itemType, key);
        const inventoryCount = gameState.getItemCount(data.seedId);
        const bloomholdCount = bloomholdUnlocked ? gameState.getBloomholdItemCount(data.seedId) : 0;
        const totalCount = inventoryCount + bloomholdCount;
        const isLocked = skill.level < data.level;
        const noSeeds = totalCount < data.seedCost;

        // For plant all, check if we can plant in at least one plot
        const canPlantCount = isPlantAll ? Math.min(emptyCount, Math.floor(totalCount / data.seedCost)) : 0;
        const plantAllDisabled = isPlantAll && (isLocked || canPlantCount === 0);

        const seedDisplay = bloomholdCount > 0
            ? `${totalCount} (${inventoryCount}+${bloomholdCount}🌱)`
            : `${totalCount}`;

        let iconHtml;
        if (data.sprite) {
            iconHtml = `<span class="plant-icon ${itemType === 'tree' ? 'tree-icon' : 'crop-icon'}">${getTreeSprite(data, 0.75)}</span>`;
        } else {
            iconHtml = `<span class="plant-icon" style="background-color: ${data.color};">${icon}</span>`;
        }

        // Different onclick for single plot vs plant all
        let onclick = '';
        let disabled = false;
        if (isPlantAll) {
            disabled = plantAllDisabled;
            onclick = !disabled ? `window.farmingUI.plantAllCrops('${itemType}', '${key}')` : '';
        } else {
            disabled = !canPlant.success;
            onclick = canPlant.success ? `window.farmingUI.plantInPlot('${itemType}', '${key}')` : '';
        }

        // Show plant count for plant all modal
        const countDisplay = isPlantAll && !isLocked
            ? `<span class="plant-count">${canPlantCount}/${emptyCount}</span>`
            : '';

        return `
            <div class="plant-option ${disabled ? 'disabled' : ''}"
                 onclick="${onclick}">
                ${iconHtml}
                <div class="plant-info">
                    <span class="plant-name">${data.name}</span>
                    <span class="plant-time">${formatGrowthTime(data.growthTime)} • ${formatNumber(data.xp)} XP</span>
                </div>
                <div class="plant-seeds ${noSeeds ? 'no-seeds' : 'has-seeds'}">
                    ${seedDisplay}/${data.seedCost}
                </div>
                ${countDisplay}
                ${isLocked ? `<span class="plant-lock">Lv ${data.level}</span>` : ''}
            </div>
        `;
    }).join('');
}

export function plantInPlot(cropType, cropId) {
    if (selectedPlotIndex === null) return;

    const result = farmingSkill.plantInPlot(selectedPlotIndex, cropType, cropId);

    if (!result.success) {
        toastWarning(result.reason);
    }

    selectedPlotIndex = null;
    renderFarmingUI();
}

export function openPlantAllModal() {
    plantAllModalOpen = true;
    renderFarmingUI();
}

export function closePlantAllModal() {
    plantAllModalOpen = false;
    plantModalTab = 'crops'; // Reset to default tab
    renderFarmingUI();
}

export function plantAllCrops(cropType, cropId) {
    const result = farmingSkill.plantAll(cropType, cropId);

    if (!result.success) {
        toastWarning(result.reason);
    } else {
        logger.game(`Planted ${result.planted} plots`);
    }

    plantAllModalOpen = false;
    renderFarmingUI();
}

export function harvestPlot(plotIndex) {
    const result = farmingSkill.harvestPlot(plotIndex);

    if (!result.success) {
        toastWarning(result.reason);
    }

    renderFarmingUI();
}

export function removePlant(plotIndex) {
    const result = farmingSkill.removePlant(plotIndex);

    if (!result.success) {
        toastWarning(result.reason);
    }

    renderFarmingUI();
}

export function harvestAll() {
    farmingSkill.harvestAll();
    renderFarmingUI();
}

export function toggleAutoHarvest(enabled) {
    gameState.updateFarmSettings({ autoHarvestEnabled: enabled });
    renderFarmingUI();
}

export function setAutoHarvestDestination(destination) {
    gameState.updateFarmSettings({ autoHarvestDestination: destination });
    renderFarmingUI();
}

export function toggleRakingMinimized() {
    rakingMinimized = !rakingMinimized;
    localStorage.setItem(RAKING_MIN_KEY, rakingMinimized);
    renderFarmingUI();
}

export function toggleToolbar() {
    toolbarExpanded = !toolbarExpanded;
    localStorage.setItem(TOOLBAR_KEY, toolbarExpanded);
    renderFarmingUI();
}

export function toggleAutoPlant(enabled) {
    gameState.updateFarmSettings({ autoPlantEnabled: enabled });
    renderFarmingUI();
}

export function setAutoPlantMode(mode) {
    gameState.updateFarmSettings({ autoPlantMode: mode });
    renderFarmingUI();
}

export function setAutoPlantSeed(value) {
    if (!value) {
        gameState.updateFarmSettings({ autoPlantCropType: null, autoPlantCropId: null });
    } else {
        const [cropType, cropId] = value.split(':');
        gameState.updateFarmSettings({ autoPlantCropType: cropType, autoPlantCropId: cropId });
    }
    renderFarmingUI();
}

export function toggleSeedPicker() {
    const dropdown = document.querySelector('.seed-picker-dropdown');
    if (!dropdown) return;
    const isOpen = dropdown.style.display !== 'none';
    dropdown.style.display = isOpen ? 'none' : 'block';
}

export function upgradeTool(toolId) {
    const result = farmingSkill.upgradeTool(toolId);

    if (result.success) {
        renderFarmingUI();
    } else {
        toastWarning(result.reason);
    }
}

export function updateFarmingUI() {
    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.FARMING);
    const xpPerHour = getXPPerHour(SKILLS.FARMING);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.FARMING);

    const xpTotalEl = document.querySelector('.farming-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.farming-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.farming-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    updateCompactHeaderProgress('.farming-interface', SKILLS.FARMING);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.farming-interface');

    // Update BloomHold count live in toolbar
    const bloomholdCount = document.querySelector('.toolbar-bloomhold-count .chip-value');
    if (bloomholdCount && gameState.isBloomholdUnlocked()) {
        const stats = getBloomholdStats();
        bloomholdCount.textContent = stats.usedSlots;
    }

    // Update raking progress if active
    const action = gameState.getCurrentAction();
    if (action?.type === 'farming' && action?.subType === 'raking') {
        const progressPct = `${action.progress * 100}%`;
        const tileFill = document.querySelector('.raking-tile-progress-fill');
        if (tileFill) tileFill.style.width = progressPct;
    }

    // Update plot timers and progress if on plots tab
    if (currentTab === 'plots') {
        const plots = gameState.getFarmPlots();
        if (!plots) return;

        let needsRerender = false;

        for (let i = 0; i < FARM_CONFIG.MAX_PLOTS; i++) {
            const plot = plots[i];
            if (!plot || plot.state !== 'growing') continue;

            const plotCard = document.querySelector(`.plots-grid .plot-card:nth-child(${i + 1})`);
            if (!plotCard) continue;

            const timeRemaining = farmingSkill.getTimeRemaining(i);
            const plotProgress = farmingSkill.checkPlotProgress(i) || 0;

            // If plot just became ready (checkPlotProgress changed state), need to re-render
            if (plot.state === 'ready') {
                needsRerender = true;
                continue;
            }

            const timerEl = plotCard.querySelector('.plot-timer');
            if (timerEl) {
                timerEl.textContent = formatTimeRemaining(timeRemaining);
            }

            const plotProgressFill = plotCard.querySelector('.plot-progress-fill');
            if (plotProgressFill) {
                plotProgressFill.style.width = `${plotProgress * 100}%`;
            }
        }

        // Re-render if any plots just became ready (skip if modal is open to avoid flash)
        if (needsRerender && selectedPlotIndex === null && !plantAllModalOpen) {
            renderFarmingUI();
        }
    }
}

export function initFarmingUI() {
    // Setup raking completion callback
    const originalRakingCallback = farmingSkill.onRakingComplete;
    farmingSkill.onRakingComplete = (xpResult) => {
        originalRakingCallback.call(farmingSkill, xpResult);
        if (getCurrentSkillId() === SKILLS.FARMING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            // Skip full re-render if modal is open or at high speed
            if (speedMult <= 1 && selectedPlotIndex === null && !plantAllModalOpen) {
                renderFarmingUI();
            }
        }
    };

    // Setup harvest completion callback
    const originalHarvestCallback = farmingSkill.onHarvestComplete;
    farmingSkill.onHarvestComplete = (cropData, xpResult) => {
        originalHarvestCallback.call(farmingSkill, cropData, xpResult);
        if (getCurrentSkillId() === SKILLS.FARMING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            // Skip full re-render if modal is open or at high speed
            if (speedMult <= 1 && selectedPlotIndex === null && !plantAllModalOpen) {
                renderFarmingUI();
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.farmingUI = {
        toggleRaking,
        toggleRakingMinimized,
        toggleToolbar,
        upgradeTool,
        switchTab,
        openPlantModal,
        closePlantModal,
        switchPlantModalTab,
        plantInPlot,
        harvestPlot,
        harvestAll,
        removePlant,
        toggleAutoHarvest,
        toggleAutoPlant,
        setAutoPlantMode,
        setAutoPlantSeed,
        toggleSeedPicker,
        setAutoHarvestDestination,
        openPlantAllModal,
        closePlantAllModal,
        plantAllCrops
    };
}

export default {
    renderFarmingUI,
    toggleRaking,
    upgradeTool,
    updateFarmingUI,
    initFarmingUI,
    switchTab,
    openPlantModal,
    closePlantModal,
    switchPlantModalTab,
    plantInPlot,
    harvestPlot,
    harvestAll,
    removePlant,
    toggleAutoHarvest,
    toggleAutoPlant,
    setAutoHarvestDestination
};
